import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import './CampaignStatusPage.css';

// 时间转换函数：UTC 转 PST
// 注意：数据库中有两种时间格式：
// - scheduled_at: ISO 格式 "2026-02-13T17:04:36.724Z" (带 Z 后缀，正确标识为 UTC)
// - executed_at/created_at: SQLite CURRENT_TIMESTAMP 格式 "2026-02-13 07:41:10" (UTC 但无时区标识)
// 对于无时区标识的格式，new Date() 会将其当作本地时间解析，导致转换错误
// 因此需要先统一将时间标准化为 UTC
function convertToPST(utcTimeString) {
  if (!utcTimeString) return 'N/A';
  
  try {
    // 标准化：如果时间字符串没有时区标识（无 Z、无 + 偏移），视为 UTC 并补上 Z
    let normalized = utcTimeString;
    if (typeof utcTimeString === 'string' && 
        !utcTimeString.endsWith('Z') && 
        !utcTimeString.includes('+') && 
        !/T\d{2}:\d{2}:\d{2}\.\d+[-+]/.test(utcTimeString)) {
      // 替换空格为 T（SQLite 格式 "YYYY-MM-DD HH:MM:SS" → ISO 格式），然后加 Z
      normalized = utcTimeString.replace(' ', 'T') + 'Z';
    }
    
    const utcDate = new Date(normalized);
    if (isNaN(utcDate.getTime())) return utcTimeString;
    
    return utcDate.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  } catch (e) {
    return utcTimeString;
  }
}

// 标准化时间字符串为 UTC（处理 SQLite CURRENT_TIMESTAMP 无时区标识的情况）
function normalizeToUTC(timeString) {
  if (!timeString || typeof timeString !== 'string') return timeString;
  if (!timeString.endsWith('Z') && 
      !timeString.includes('+') && 
      !/T\d{2}:\d{2}:\d{2}\.\d+[-+]/.test(timeString)) {
    return timeString.replace(' ', 'T') + 'Z';
  }
  return timeString;
}

// 计算相对时间
function getRelativeTime(scheduledTime) {
  if (!scheduledTime) return 'N/A';
  
  try {
    const scheduled = new Date(normalizeToUTC(scheduledTime));
    const now = new Date();
    const diffMs = scheduled - now;
    const diffMins = Math.floor(diffMs / 1000 / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMs < 0) {
      const pastMins = Math.abs(diffMins);
      if (pastMins < 60) return `${pastMins} 分钟前`;
      const pastHours = Math.floor(pastMins / 60);
      if (pastHours < 24) return `${pastHours} 小时前`;
      return `${Math.floor(pastHours / 24)} 天前`;
    } else {
      if (diffMins < 60) return `${diffMins} 分钟后`;
      if (diffHours < 24) return `${diffHours} 小时后`;
      return `${diffDays} 天后`;
    }
  } catch (e) {
    return 'N/A';
  }
}

function getConnectionStatusDisplay(connection) {
  if (!connection?.state) return null;

  if (connection.state === 'pending') {
    return {
      tone: 'pending',
      label: connection.shared_pending
        ? '账号已有 Pending 邀请'
        : '邀请 Pending',
      detail: connection.shared_pending
        ? '本 Campaign 未重复发送，正在等待同一邀请被接受'
        : '连接邀请已发出，正在等待对方接受',
      time: connection.invitation_sent_at
        ? `发送时间：${convertToPST(connection.invitation_sent_at)}`
        : null
    };
  }

  if (connection.state === 'withdrawn') {
    const reconnectAt = connection.reconnect_after
      ? new Date(normalizeToUTC(connection.reconnect_after))
      : null;
    const coolingDown = reconnectAt && reconnectAt > new Date();
    return {
      tone: coolingDown ? 'cooldown' : 'withdrawn',
      label: coolingDown ? '撤回后冷静期中' : '已撤回',
      detail: coolingDown
        ? '该账号不会再次发送；当前 Campaign 的此人已终止'
        : '撤回记录仍保留，但 21 天冷静期已经结束',
      time: connection.reconnect_after
        ? `冷静期至：${convertToPST(connection.reconnect_after)}`
        : null
    };
  }

  if (connection.state === 'connected') {
    return {
      tone: 'connected',
      label: '已连接',
      detail: '该账号与此人已经是一度好友',
      time: null
    };
  }

  if (connection.state === 'sending') {
    return {
      tone: 'sending',
      label: '正在发送邀请',
      detail: '同账号下已有任务取得发送权，本 Campaign 不会并发重复发送',
      time: null
    };
  }

  return null;
}

function CampaignStatusPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/campaigns/${id}/status`);
      if (response.data.success) {
        setStatusData(response.data);
        setLastUpdate(new Date());
        setError(null);
      } else {
        setError(response.data.message || 'Failed to fetch campaign status');
      }
    } catch (err) {
      console.error('Error fetching campaign status:', err);
      setError(err.response?.data?.message || 'Failed to fetch campaign status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // 每 2 秒更新一次
    const interval = setInterval(fetchStatus, 2000);
    return () => clearInterval(interval);
  }, [id]);

  if (loading && !statusData) {
    return (
      <div className="campaign-status-page">
        <div className="loading">加载中...</div>
      </div>
    );
  }

  if (error && !statusData) {
    return (
      <div className="campaign-status-page">
        <div className="error">
          <p>❌ 错误: {error}</p>
          <button onClick={() => navigate('/campaigns')} className="btn btn-primary">
            返回 Campaign 列表
          </button>
        </div>
      </div>
    );
  }

  if (!statusData) {
    return null;
  }

  const { campaign, progress, tasks, targets, target_details } = statusData;

  const getStatusIcon = (status) => {
    const icons = {
      'pending': '⏳',
      'in_progress': '🔄',
      'completed': '✅',
      'failed': '❌',
      'withdrawn': '↩️',
      'skipped': '⏭️'
    };
    return icons[status] || '❓';
  };

  return (
    <div className="campaign-status-page">
      <div className="status-header">
        <button onClick={() => navigate('/campaigns')} className="btn btn-secondary back-btn">
          ← 返回
        </button>
        <div className="header-content">
          <h1>📋 Campaign 进度监控</h1>
          <h2>{campaign.name}</h2>
          {lastUpdate && (
            <p className="last-update">
              最后更新: {lastUpdate.toLocaleTimeString()}
            </p>
          )}
        </div>
      </div>

      <div className="status-summary">
        <div className="summary-card campaign-info">
          <h3>Campaign 信息</h3>
          <div className="info-item">
            <span className="label">状态:</span>
            <span className={`value status-${campaign.status}`}>
              {getStatusIcon(campaign.status)} {campaign.status}
            </span>
          </div>
          {campaign.description && (
            <div className="info-item">
              <span className="label">描述:</span>
              <span className="value">{campaign.description}</span>
            </div>
          )}
        </div>

        <div className="summary-card progress-card">
          <h3>总体进度</h3>
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progress}%` }}
              >
                {progress}%
              </div>
            </div>
            <p className="progress-text">
              {tasks.completed + tasks.failed + tasks.skipped} / {tasks.total} tasks 已处理
            </p>
          </div>
        </div>

        <div className="summary-card tasks-card">
          <h3>Tasks 统计</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-icon">⏳</span>
              <span className="stat-value">{tasks.pending}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">🔄</span>
              <span className="stat-value">{tasks.executing}</span>
              <span className="stat-label">Executing</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">🟢</span>
              <span className="stat-value">{tasks.completed}</span>
              <span className="stat-label">Completed</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">🔴</span>
              <span className="stat-value">{tasks.failed}</span>
              <span className="stat-label">Failed</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">⏭️</span>
              <span className="stat-value">{tasks.skipped}</span>
              <span className="stat-label">Skipped</span>
            </div>
          </div>
        </div>

        <div className="summary-card targets-card">
          <h3>Targets 统计</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-icon">✅</span>
              <span className="stat-value">{targets.completed}</span>
              <span className="stat-label">Completed</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">🔄</span>
              <span className="stat-value">{targets.in_progress}</span>
              <span className="stat-label">In Progress</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">⏳</span>
              <span className="stat-value">{targets.pending}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">❌</span>
              <span className="stat-value">{targets.failed}</span>
              <span className="stat-label">Failed</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">↩️</span>
              <span className="stat-value">{targets.withdrawn}</span>
              <span className="stat-label">Withdrawn</span>
            </div>
          </div>
        </div>
      </div>

      <div className="targets-details">
        <h3>📊 Target 详细进度 ({target_details.length} 个)</h3>
        <div className="targets-list">
          {target_details.map(target => {
            const connectionDisplay = getConnectionStatusDisplay(target.connection);
            return (
            <div key={target.id} className="target-card">
              <div className="target-header">
                <span className="target-icon">{getStatusIcon(target.status)}</span>
                <h4>{target.name}</h4>
              </div>
              <div className="target-info">
                <div className="info-row">
                  <span className="label">状态:</span>
                  <span className="value">{target.status}</span>
                  <span className="separator">|</span>
                  <span className="label">步骤:</span>
                  <span className="value">{target.current_step}</span>
                </div>
                <div className="info-row">
                  <span className="label">进度:</span>
                  <div className="target-progress-bar">
                    <div 
                      className="target-progress-fill" 
                      style={{ width: `${target.progress}%` }}
                    />
                    <span className="progress-text">
                      {target.progress}% ({
                        target.tasks.completed +
                        target.tasks.failed +
                        target.tasks.skipped
                      }/{target.tasks.total} tasks)
                    </span>
                  </div>
                </div>
                <div className="info-row">
                  <span className="label">Tasks:</span>
                  <span className="task-stats">
                    <span className="task-stat">⏳{target.tasks.pending}</span>
                    <span className="task-stat">🟢{target.tasks.completed}</span>
                    <span className="task-stat">🔴{target.tasks.failed}</span>
                    <span className="task-stat">🔄{target.tasks.executing}</span>
                    <span className="task-stat">⏭️{target.tasks.skipped}</span>
                  </span>
                </div>
                {connectionDisplay && (
                  <div className={`connection-state connection-state-${connectionDisplay.tone}`}>
                    <div className="connection-state-title">
                      {connectionDisplay.label}
                    </div>
                    <div className="connection-state-detail">
                      {connectionDisplay.detail}
                    </div>
                    {connectionDisplay.time && (
                      <div className="connection-state-time">
                        {connectionDisplay.time}
                      </div>
                    )}
                  </div>
                )}
                {target.next_task && (
                  <div className="info-row">
                    <span className="label">下一个任务:</span>
                    <span className="value">
                      {target.next_task.type} ({getRelativeTime(target.next_task.scheduled_at)})
                    </span>
                    <span className="next-task-time">
                      PST: {convertToPST(target.next_task.scheduled_at)}
                    </span>
                  </div>
                )}
                {target.task_list && target.task_list.length > 0 && (
                  <div className="task-schedule-section">
                    <div className="label task-schedule-label">任务计划时间:</div>
                    <div className="task-schedule-list">
                      {target.task_list.map((task, idx) => (
                        <div key={task.id || idx} className="task-schedule-item" data-status={task.status}>
                          <span className={`task-status-icon task-status-${task.status}`}>
                            {getStatusIcon(task.status)}
                          </span>
                          <span className="task-type">{task.action_type}</span>
                          <span className="task-status-badge">{task.status}</span>
                          {task.scheduled_at && (
                            <span className="task-time">
                              {getRelativeTime(task.scheduled_at)} (PST: {convertToPST(task.scheduled_at)})
                            </span>
                          )}
                          {task.executed_at && (
                            <span className="task-executed">
                              已执行: {convertToPST(task.executed_at)}
                            </span>
                          )}
                          {task.error_message && (
                            <span className="task-error-message">
                              {task.error_message}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default CampaignStatusPage;
